'use client';

// LIVE Weekend League controller — the production replacement for the mock
// hook on the events tab. Maps the backend tournament (GET /current) onto the
// prototype's phase/controller contract so the presentational components work
// unchanged; entry goes through the real endpoint. The mock hook stays behind
// /dev/wl for design iteration.
//
// Not yet live-driven (lands with the wl:* socket client): gameplay, check-in,
// standings and your rank. Until then `playable` is false — the screen renders
// live phases informationally and never routes users into the mock game — and
// the qualifier board stays empty rather than showing invented players.

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackWlCheckinCompleted, trackWlEntryCompleted } from '@/lib/analytics/game-events';
import { toast } from 'sonner';
import { checkinWeekendLeague, enterWeekendLeague, getWeekendLeagueCurrent, getWeekendLeagueStandings } from '@/lib/api/endpoints';
import { queryKeys } from '@/lib/queries/queryKeys';
import { useAuthStore } from '@/stores/auth.store';
import { useLocale } from '@/contexts/LocaleContext';
import type { components } from '@/types/api.generated';
import { LATE_JOIN_MS, QP_TARGET } from './constants';
import { syncWlClock, wlNow } from './wlClock';
import { getMilestones } from './mock-data';
import type { LeaguePhase, Milestone } from './types';
import type { WeekendLeagueController } from './use-weekend-league';

type WlCurrent = components['schemas']['WlCurrentResponse'];
type WlStatus = NonNullable<WlCurrent['tournament']>['status'];

// Statuses where the tournament is actually running and the screen should
// refresh fast; everything else can poll lazily.
const HOT_STATUSES: ReadonlySet<string> = new Set([
  'checkin', 'game_live', 'break', 'final_checkin', 'final_live', 'paused',
]);

function phaseFromStatus(status: WlStatus | undefined): LeaguePhase {
  switch (status) {
    case 'entry_open':
    case 'entry_closed': // same countdown card; the claim CTA locks via canEnter
      return 'entry_open';
    case 'checkin':
    case 'game_live':
    case 'break':
    case 'paused':
      return 'qualifier_live';
    case 'qualifier_done':
    case 'final_checkin':
      return 'qualifier_done';
    case 'final_live':
      return 'playoffs_live';
    case 'completed':
      return 'completed';
    default:
      // scheduled / content_pending / ready — the week hasn't opened yet.
      // cancelled / voided / no tournament fall back to the next event's
      // calendar (handled by the milestone fallback below).
      return 'upcoming';
  }
}

function toMilestone(
  key: Milestone['key'],
  label: string,
  iso: string | null | undefined,
  fallback: Milestone,
): Milestone {
  if (!iso) return fallback;
  const targetMs = Date.parse(iso);
  if (Number.isNaN(targetMs)) return fallback;
  return { key, label, targetMs };
}

/** Live-only signals layered on top of the prototype controller contract. */
export interface WeekendLeagueLiveExtras {
  /** Marks a backend-driven controller (the screen uses it to trust server fields). */
  live: true;
  isLoading: boolean;
  /** True once THIS mount has a fresh /current response — cached data during
   *  a background refetch must not feed analytics (stale phase/entered). */
  statusFresh: boolean;
  isError: boolean;
  refetch: () => void;
  tournamentId: string | null;
  /** Raw backend tournament status (finer-grained than the screen phase). */
  status: string | null;
  /** Authoritative qualifier kickoff (ms) from the live row; null when the
      row carries no real timestamp — never the synthetic calendar fallback. */
  kickoffMs: number | null;
  /** Role-appropriate check-in state for the current window. */
  checkedIn: boolean;
  /** Live count of players already checked in (public payload). */
  checkedInCount: number;
  /** Server break deadline (epoch ms) between games; null outside breaks. */
  breakUntilMs: number | null;
  /** How far behind live the spectator stream runs — spectator countdowns
   *  shift by this so they land when the delayed stream actually resumes. */
  spectatorDelayMs: number;
  /** Your rank in the newest finished game (board is top-24 only). */
  lastGameRank: number | null;
  /** 0-based game currently running / next to run (public payload). */
  currentGameIndex: number;
  checkinLeague: () => void;
  checkinPending: boolean;
  /** Non-null while THIS user could still late-join the running game: the
   *  grace deadline (epoch ms). Time-bounding is the consumer's job (the
   *  banner ticks and hides itself); the server re-authorizes on check-in. */
  lateJoinUntilMs: number | null;
  /** Running QP balance (server truth, resets when a ticket is claimed). */
  qp: number;
  qpTarget: number;
  /** Server's word on entry eligibility (`you.qp.qualified`). */
  qpQualified: boolean;
  /** The entry window is open right now and the caller hasn't claimed yet. */
  canEnter: boolean;
  /** Actually won the event (distinct from `qualified` = reached the final). */
  champion: boolean;
  /** Gameplay is wired — false until the wl:* socket client lands, so live
      phases render informationally instead of launching the mock game. */
  playable: boolean;
}

export type WeekendLeagueLiveController = WeekendLeagueController & WeekendLeagueLiveExtras;

const noop = () => {};

export function useWeekendLeagueLive(): WeekendLeagueLiveController {
  const authStatus = useAuthStore((state) => state.status);
  const queryClient = useQueryClient();
  const { t } = useLocale();
  const [nowMs] = useState(() => Date.now());

  const query = useQuery({
    queryKey: queryKeys.weekendLeague.current(),
    queryFn: getWeekendLeagueCurrent,
    enabled: authStatus === 'authenticated',
    staleTime: 0,
    refetchInterval: (q) => {
      const t = q.state.data?.tournament;
      const status = t?.status;
      if (status && HOT_STATUSES.has(status)) return 5_000;
      // Near ANY phase boundary poll fast: a lazy 60s cadence left the entry
      // card frozen at 00:00 for up to a minute after the window closed
      // (owner playtest report) — the flip to check-in must land in seconds.
      const now = wlNow();
      const boundaries = [t?.entry_opens_at, t?.entry_closes_at, t?.qualifier_starts_at, t?.final_starts_at]
        .map((iso) => (iso ? Date.parse(iso) : Number.NaN))
        .filter((ms) => Number.isFinite(ms) && ms > now - 30_000);
      const nearest = boundaries.length > 0 ? Math.min(...boundaries) : null;
      // Straddling a boundary (10s either side) the status flip must land in
      // ~a second — this is the moment the countdown sits at 00:00 waiting
      // for the card to swap.
      if (nearest != null && Math.abs(nearest - now) < 10_000) return 1_000;
      if (nearest != null && nearest - now < 90_000) return 3_000;
      return 60_000;
    },
  });

  const tournament = query.data?.tournament ?? null;
  const standingsQuery = useQuery({
    queryKey: [...queryKeys.weekendLeague.all, 'standings'],
    queryFn: getWeekendLeagueStandings,
    enabled: authStatus === 'authenticated',
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  useEffect(() => {
    const serverNow = tournament?.server_now_ms;
    if (typeof serverNow === 'number') syncWlClock(serverNow);
  }, [tournament?.server_now_ms]);
  const you = query.data?.you ?? null;
  const status = tournament?.status;
  const phase = phaseFromStatus(status);
  const hasEntered = you?.entered ?? false;
  const canEnter = status === 'entry_open' && !hasEntered;

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.weekendLeague.all }),
    [queryClient],
  );

  const enterMutation = useMutation({
    mutationFn: enterWeekendLeague,
    onSuccess: (res) => {
      if (!res.entered && !res.already_entered) {
        toast.error(t('weekendLeague.entryClosedToast'));
      }
      if (res.entered && !res.already_entered) trackWlEntryCompleted();
      invalidate();
    },
    onError: () => toast.error(t('weekendLeague.entryFailedToast')),
  });
  const { mutate: enterMutate } = enterMutation;
  const enterLeague = useCallback(() => {
    if (status !== 'entry_open') {
      toast.error(t('weekendLeague.entryClosedToast'));
      return;
    }
    enterMutate();
  }, [status, enterMutate, t]);

  const checkinMutation = useMutation({
    mutationFn: checkinWeekendLeague,
    onSuccess: (res) => {
      if (!res.checked_in && !res.already_checked_in) {
        toast.error(t('weekendLeague.checkinNotOpenToast'));
      }
      if (res.checked_in && !res.already_checked_in) {
        // Stage captured at SUBMIT time: the mutation callback re-binds on
        // rerender, so reading `status` here can misattribute a final
        // check-in that resolves after the tournament advances (review).
        trackWlCheckinCompleted(checkinStageRef.current);
      }
      invalidate();
    },
    onError: () => toast.error(t('weekendLeague.checkinFailedToast')),
  });
  const { mutate: checkinMutate } = checkinMutation;
  const checkinStageRef = useRef<'qualifier' | 'final'>('qualifier');
  const checkinLeague = useCallback(() => {
    checkinStageRef.current =
      status === 'final_checkin' || status === 'final_live' ? 'final' : 'qualifier';
    checkinMutate();
  }, [checkinMutate, status]);

  const milestones = useMemo(() => {
    // The fallback is NOT invented: the league runs on a fixed weekly calendar
    // (Fri close / Sat qualifier / Sun final, Georgia time) shared with the
    // backend scheduler. With no tournament row — or a cancelled/voided one —
    // the next occurrence of that calendar IS the next event; the reconciler
    // creates its row ahead of time and the poll swaps in real timestamps.
    const fallback = getMilestones(nowMs);
    if (!tournament || tournament.status === 'cancelled' || tournament.status === 'voided') {
      return fallback;
    }
    return {
      entry: toMilestone('entry', 'Entry closes', tournament.entry_closes_at, fallback.entry),
      qualifier: toMilestone('qualifier', 'Qualifier', tournament.qualifier_starts_at, fallback.qualifier),
      playoffs: toMilestone('playoffs', 'Playoffs', tournament.final_starts_at, fallback.playoffs),
    };
  }, [tournament, nowMs]);

  const activeMilestone: Milestone | null = useMemo(() => {
    switch (phase) {
      case 'upcoming':
        return milestones.entry;
      case 'entry_open':
        return milestones.qualifier;
      case 'qualifier_done':
        return milestones.playoffs;
      default:
        return null;
    }
  }, [milestones, phase]);

  // Server truth only — no locally simulated results in live mode.
  const champion = you?.state === 'champion';
  const qualified = champion || you?.state === 'finalist';

  // Authoritative kickoff only: null unless the live tournament row carries
  // a real qualifier timestamp — the synthetic calendar fallback must never
  // drive a countdown on the checked-in waiting screen.
  const kickoffMs = (() => {
    // Sunday phases count to the FINAL, not to Saturday's (past) qualifier —
    // the final check-in card showed no timer at all otherwise.
    const source = status === 'qualifier_done' || status === 'final_checkin' || status === 'final_live'
      ? tournament?.final_starts_at
      : tournament?.qualifier_starts_at;
    if (!source) return null;
    const ms = Date.parse(source);
    return Number.isFinite(ms) ? ms : null;
  })();

  const lateJoinUntilMs = (() => {
    if (kickoffMs == null) return null;
    if (status === 'game_live' && (tournament?.current_game_index ?? 0) === 0
        && you?.state === 'entered' && !(you?.checked_in ?? false)) {
      return kickoffMs + LATE_JOIN_MS;
    }
    if ((status === 'final_checkin' || status === 'final_live')
        && (you?.state === 'finalist' || you?.state === 'no_show')
        && !(you?.final_checked_in ?? false)) {
      return kickoffMs + LATE_JOIN_MS;
    }
    return null;
  })();

  return {
    phase,
    hasEntered,
    qualified,
    kickoffMs,
    milestones,
    activeMilestone,
    leaderboard: (standingsQuery.data?.entries ?? []).map((e) => ({
      id: e.user_id,
      username: e.nickname ?? '—',
      avatar: e.avatar_url ?? 'avatar-1',
      tier: e.tier,
      country: e.country ?? 'GE',
      score: e.points,
    })),
    yourRank: 0,
    bracket: null,
    // null (not 0) when the tournament is unknown — logged-out visitors have
    // no /current access and must not see "0 players registered" (418 were
    // entered while the page claimed 0, 2026-09-25).
    registered: tournament ? tournament.registered_count ?? 0 : null,
    checkedInCount: tournament?.checked_in_count ?? 0,
    breakUntilMs: tournament?.break_until_ms ?? null,
    spectatorDelayMs: tournament?.spectator_delay_ms ?? 30_000,
    lastGameRank: you?.last_game_rank ?? null,
    currentGameIndex: tournament?.current_game_index ?? 0,
    session: null,
    playedOutcome: null,
    playoffOutcome: null,
    setPhase: noop,
    setEntered: noop,
    setQualified: noop,
    enterLeague,
    startQualifierGame: noop,
    startPlayoffGame: noop,
    cancelGame: noop,
    finishQualifier: noop,
    finishPlayoff: noop,
    live: true,
    isLoading: query.isLoading,
    statusFresh: query.isSuccess && query.isFetchedAfterMount,
    // Fatal only when there's nothing to show — a failed background poll on
    // top of usable data must not blank the screen into an error card.
    isError: query.isError && query.data === undefined,
    refetch: () => void query.refetch(),
    tournamentId: tournament?.id ?? null,
    status: status ?? null,
    checkedIn: status === 'final_checkin' || status === 'final_live'
      ? (you?.final_checked_in ?? false)
      : (you?.checked_in ?? false),
    checkinLeague,
    checkinPending: checkinMutation.isPending,
    lateJoinUntilMs,
    qp: you?.qp.points ?? 0,
    qpTarget: tournament?.qp_target ?? QP_TARGET,
    qpQualified: you?.qp.qualified ?? false,
    canEnter,
    champion,
    playable: false,
  };
}
